import moment from 'moment-timezone';
import _ from 'lodash';

import * as validationErrorRules from '../constants/validationErrorRules';
import * as validationWarningRules from '../constants/validationWarningRules';
import validationMessages from '../constants/validationMessages';
import firebase from "./fire";
import firebaseUtils from './FirebaseUtils';

export default class ValidationUtils {

    // validation main functions

    static validate(values, formState) {
        return ValidationUtils.checkRules(values, formState);
    }

    static asyncValidate(values, dispatch, formState) {
        return ValidationUtils.checkAsyncRules(values, formState);
    }

    static warn(values, formState) {
        return ValidationUtils.checkRules(values, formState, true);
    }

    static asyncWarn(values, dispatch, formState) {
        return ValidationUtils.checkAsyncRules(values, formState, true);
    }

    static checkRules(model, formState, isWarning) {
        const validationRules = isWarning ? validationWarningRules : validationErrorRules;
        if (Object.keys(validationRules).includes(formState.form)) {
            const formRules = validationRules[formState.form];
            return Object.entries(formRules).reduce((errorAcc, [fieldKey, fieldRules]) => {
                Object.entries(fieldRules).filter(([ruleKey]) => !ruleKey.endsWith('Async')).forEach(([ruleKey, ruleValue]) => {

                    const value = fieldKey.includes('.')
                        ? fieldKey.split('.').reduce((acc, key) => typeof acc === 'object' ? acc[key] : undefined, model)
                        : model[fieldKey];

                    const valid = ValidationUtils[ruleKey](value, ruleValue, model, isWarning);

                    if (!valid) {
                        if (fieldKey.includes('.')) {
                            fieldKey.split('.').reduce((innerAcc, key, index, splitKey) => {
                                if (!innerAcc[key] && key !== splitKey[splitKey.length-1]) {
                                    innerAcc[key] = {};
                                } else if (key === splitKey[splitKey.length-1]) {
                                    innerAcc[key] = validationMessages[ruleKey](value, ruleValue);
                                }
                                return innerAcc[key];
                            }, errorAcc);
                        } else {
                            errorAcc[fieldKey] = validationMessages[ruleKey](value, ruleValue);
                        }
                    }
                });
                return errorAcc;
            }, {});
        }
    }

    static async checkAsyncRules(model, formState, isWarning) {
        const validationRules = isWarning ? validationWarningRules : validationErrorRules;
        if (Object.keys(validationRules).includes(formState.form)) {
            const formRules = Object.entries(validationRules[formState.form]);
            const errors = {};

            for (const [fieldKey, fieldRules] of formRules) {
                const asyncFieldRules = Object.entries(fieldRules).filter(([ruleKey]) => ruleKey.endsWith('Async'));

                for (const [ruleKey, ruleValue] of asyncFieldRules) {
                    const value = fieldKey.includes('.')
                        ? fieldKey.split('.').reduce((acc, key) => typeof acc === 'object' ? acc[key] : undefined, model)
                        : model[fieldKey];
                    
                    let valid = true;
                    try {
                        valid = await ValidationUtils[ruleKey](value, ruleValue, model, isWarning);
                    } catch (err) {
                        // Treat any error as invalid
                        if (typeof err !== 'boolean') {
                            console.warn(`An unexpected error ocurred during validation on field ${fieldKey} in ${formState.form}. Error displayed below`);
                            console.error(err);
                        }
                        valid = false;
                    }

                    if (!valid) {
                        if (fieldKey.includes('.')) {
                            fieldKey.split('.').reduce((innerAcc, key, index, splitKey) => {
                                if (!innerAcc[key] && key !== splitKey[splitKey.length-1]) {
                                    innerAcc[key] = {};
                                } else if (key === splitKey[splitKey.length-1]) {
                                    innerAcc[key] = (!innerAcc[key] ? "" : innerAcc[key] + " ") + validationMessages[ruleKey](value, ruleValue);
                                }
                                return innerAcc[key];
                            }, errors);
                        } else {
                            errors[fieldKey] = (!errors[fieldKey] ? "" : errors[fieldKey] + " ") + validationMessages[ruleKey](value, ruleValue);
                        }
                    }
                }
            }

            if (Object.keys(errors).length > 0) {
                throw errors;
            }
        }
    }


    // validation rules

    static charsRemaining(value, ruleValue, model, isWarning) {
        if (isWarning) {
            return false;
        } else {
            return !value || !(value.length > ruleValue);
        }
    }

    static isInteger(value) {
        return !value || (!Number.isNaN(value) && `${value}` === `${Number.parseInt(value, 10)}`);
    }

    static isRequired(value) {
        if (Array.isArray(value)) {
            return !!value.length;
        } else {
            return !!value;
        }
    }

    static isRequiredIfFieldHasValue(value, ruleValue, model) {
        const field = _.get(model, `${ruleValue.field}`);
        const fieldValues = ruleValue.values;
        let fieldHasValue = false;

        if (field && Array.isArray(field)) {
            field.some(option => fieldValues.includes(option));
        } else if (field && !Array.isArray(field) && fieldValues.includes(field)) {
            fieldHasValue = true;
        }

        if (fieldHasValue) {
            if (Array.isArray(value)) {
                return !!value.length;
            } else {
                return !!value;
            }
        } else {
            return true;
        }
    }

    static isRequiredIfModuleSelected(value, ruleValue, model) {
        if (model.modules && model.modules.includes(ruleValue)) {
            if (Array.isArray(value)) {
                return !!value.length;
            } else if (typeof value === 'object') {
                return !!Object.keys(value).length;
            } else {
                return !!value;
            }
        } else {
            return true;
        }
    }

    static isRequiredTrim(value) {
        return !!value && value.trim() !== '';
    }

    static isValidDate(value) {
        return !value || moment(value).isValid();
    }

    static isValidEmail(value) {
        const emailRegex = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
        return value && emailRegex.test(value.toLowerCase());
    }

    static isValidMediaUrl(value) {
        const urlRegex = /^(http|https):\/\/www\.youtube\.com\/watch\?v=[^ "]+$/;
        return urlRegex.test(value);
    }

    static isValidTimezone(value) {
        return !value || moment.tz.names().includes(value);
    }

    static matchesPattern(value, ruleValue) {
        return !value || ruleValue.test(value);
    }

    static async isValidSpocWidthAsync(value, maxWidth) {
        if (value) {
            const url = await firebaseUtils.getImageUrl(firebase, value);
            return new Promise((resolve, reject) => {
                const image = new Image();
                image.src = url;
                image.onload = function() {
                    resolve(this.width <= maxWidth);
                };
                image.onerror = reject;
            });
        } else {
            return true;
        }
    }

    static async isValidSpocHeightAsync(value, maxHeight) {
        if (value) {
            const url = await firebaseUtils.getImageUrl(firebase, value);
            return new Promise((resolve, reject) => {
                const image = new Image();
                image.src = url;
                image.onload = function() {
                    resolve(this.height <= maxHeight);
                };
                image.onerror = reject;
            });
        } else {
            return true;
        }
    }
}



// WEBPACK FOOTER //
// ./src/util/ValidationUtils.js